/**
 * useIncomingCall — the application-level incoming-call ring.
 *
 * Why application-level: the recipient is, by definition, NOT in the
 * consultation room when a call starts. The invite arrives on the shared
 * realtime socket (their own `user:<uid>` room), so this hook lives in the
 * authenticated shell and works from any screen.
 *
 * Truthfulness rules it enforces:
 *  - a malformed or self-addressed invite is refused before anything renders
 *    (`parseIncomingCall`), and a duplicate cannot ring twice (`reduceIncomingCall`);
 *  - the ring is started ONLY while the machine is `ringing`, and stopped on
 *    accept, decline, timeout, an authoritative end, or unmount;
 *  - if the browser refuses to play audio, `ringBlocked` is true and the UI says
 *    so — it never claims the phone is ringing;
 *  - entering the room (by any route) stops the ring for that call.
 *
 * Evidence: the stages that matter for a ringing failure
 * (`incoming.event-received`, `ring.started` / `ring.blocked`, `call.accepted`,
 * `ring.stopped`, `incoming.timeout`, `incoming.duplicate`) are recorded through
 * the SAME opt-in collector the consultation room uses, so a support report has
 * one format. Nothing is recorded unless `?rtcdebug=1`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  declineConsultationCall,
  subscribeToConsultationEnded,
  subscribeToEncounterCompleted,
  subscribeToIncomingCall,
} from '../../services/realtime';
import type { CallDeclineReasonCode } from '../../utils/callDecline';
import {
  initialIncomingCall,
  parseIncomingCall,
  reduceIncomingCall,
  roomPathFor,
  type IncomingCallMachine,
} from '../../utils/incomingCall';
import {
  RING_TIMEOUT_MS,
  primeRingtoneOnFirstGesture,
  startRingtone,
  stopRingtone,
  unlockRingtone,
} from '../../utils/ringtone';
import { createRtcDiagnostics, isRtcDiagnosticsEnabled } from '../../utils/webrtcDiagnostics';

interface Options {
  /** Authenticated uid — an invite addressed to self is refused. */
  uid?: string | null;
  /** Authenticated role — decides which room the recipient may be sent to. */
  role?: string | null;
  enabled?: boolean;
}

export function useIncomingCall({ uid, role, enabled = true }: Options) {
  const navigate = useNavigate();
  const location = useLocation();
  const [machine, setMachine] = useState<IncomingCallMachine>(initialIncomingCall);
  /** True when a ring was wanted but the browser refused to play audio. */
  const [ringBlocked, setRingBlocked] = useState(false);

  // Read the current path without making it an effect dependency for the
  // subscription (a navigation must not tear the socket listeners down).
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  /**
   * Mirror of the machine so an event handler can detect a duplicate
   * SYNCHRONOUSLY, before React re-renders — a second invite in the same tick
   * must not start a second ring. Also keeps the reducer out of the setState
   * updater, which React may invoke more than once.
   */
  const machineRef = useRef(machine);
  machineRef.current = machine;

  /** Opt-in, secret-free evidence trail (disabled unless `?rtcdebug=1`). */
  const diagnosticsRef = useRef(createRtcDiagnostics(false));
  useEffect(() => {
    const on = isRtcDiagnosticsEnabled();
    diagnosticsRef.current = createRtcDiagnostics(on);
    if (on && typeof window !== 'undefined') {
      (window as any).__bhabyIncomingDiag = diagnosticsRef.current;
    }
  }, []);

  const apply = useCallback((event: Parameters<typeof reduceIncomingCall>[1]) => {
    const prev = machineRef.current;
    const next = reduceIncomingCall(prev, event);
    if (next === prev) return false;
    machineRef.current = next; // guard against a same-tick repeat
    setMachine(next);
    return true;
  }, []);

  // ── Realtime: the invite, and the authoritative end-of-call ────────────────
  useEffect(() => {
    if (!enabled || !uid) return;

    const releaseGesture = primeRingtoneOnFirstGesture();

    const offIncoming = subscribeToIncomingCall((payload) => {
      const call = parseIncomingCall(payload, uid);
      if (!call) {
        // Never rendered: it did not identify a real call, or was addressed to
        // the recipient by the recipient.
        diagnosticsRef.current.record('incoming.rejected');
        return;
      }
      diagnosticsRef.current.record('incoming.event-received', {
        bookingId: call.bookingId,
        sessionId: call.sessionId,
      });

      // Already on this call's room: the room itself owns the UI and the media.
      if (pathRef.current === roomPathFor(call, role)) {
        diagnosticsRef.current.record('incoming.ignored', { reason: 'already-in-room' });
        return;
      }
      if (!apply({ type: 'incoming', call })) {
        diagnosticsRef.current.record('incoming.duplicate', { bookingId: call.bookingId });
      }
    });

    // Either participant hanging up, or the clinician completing the encounter,
    // must stop a ring that is still going.
    const stopFor = (payload: unknown) => {
      const bookingId = (payload as { bookingId?: unknown } | null)?.bookingId ?? null;
      apply({ type: 'ended', bookingId: bookingId as number | null });
    };
    const offEnded = subscribeToConsultationEnded(stopFor);
    const offCompleted = subscribeToEncounterCompleted(stopFor);

    return () => {
      releaseGesture();
      offIncoming();
      offEnded();
      offCompleted();
    };
  }, [enabled, uid, role, apply]);

  // ── The ring exists ONLY while the machine is ringing ─────────────────────
  //
  // Keyed on the BOOLEAN, not on the call: a second invite arriving while a ring
  // is already up must not tear the loop down and restart it, and the cleanup
  // (which stops the ring) must run exactly once — when the ring genuinely ends.
  const ringing = machine.state === 'ringing';
  useEffect(() => {
    if (!ringing) {
      stopRingtone();
      setRingBlocked(false);
      return;
    }
    diagnosticsRef.current.record('incoming.ui-shown', {
      bookingId: machineRef.current.call?.bookingId ?? null,
    });
    diagnosticsRef.current.record('ring.start-attempted');
    // startRingtone() is idempotent and reports whether audio actually began; a
    // refusal is surfaced instead of being papered over.
    const started = startRingtone();
    diagnosticsRef.current.record(started ? 'ring.started' : 'ring.blocked');
    setRingBlocked(!started);
    return () => {
      stopRingtone();
      diagnosticsRef.current.record('ring.stopped');
    };
  }, [ringing]);

  // ── An unanswered ring must not ring for ever ─────────────────────────────
  useEffect(() => {
    if (machine.state !== 'ringing') return;
    const id = setTimeout(() => {
      diagnosticsRef.current.record('incoming.timeout');
      apply({ type: 'timeout' });
    }, RING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [machine.state, machine.call, apply]);

  // ── Entering the room by any route takes the call: stop ringing ───────────
  useEffect(() => {
    if (machine.state !== 'ringing' || !machine.call) return;
    if (location.pathname === roomPathFor(machine.call, role)) {
      diagnosticsRef.current.record('call.accepted', { via: 'navigation' });
      stopRingtone();
      apply({ type: 'accept' });
    }
  }, [location.pathname, machine.state, machine.call, role, apply]);

  /** Answer: unlock audio (this click IS the gesture), stop, and open the room. */
  const accept = useCallback((): string | null => {
    const call = machine.call;
    if (!call) return null;
    unlockRingtone();
    stopRingtone();
    diagnosticsRef.current.record('call.accepted', { via: 'button' });
    apply({ type: 'accept' });
    const path = roomPathFor(call, role);
    if (path) navigate(path);
    return path;
  }, [machine.call, role, navigate, apply]);

  /**
   * Stop the RING TONE without answering or declining.
   * Used while the reason sheet is open: the phone must stop ringing the moment
   * the user reaches for Decline, while the call itself is still undecided.
   */
  const silence = useCallback(() => {
    stopRingtone();
  }, []);

  /**
   * Decline: stop the ring, tell the CALLER (server-authoritative), and record
   * that this invite was acted on.
   *
   * The local state changes immediately so the recipient's UI never lies about
   * what they just did, but the CALLER learns it from the server — never from
   * this device's guess. The reason is a stable code (`NO_REASON` by default,
   * because declining without explaining is a real choice) plus optional free
   * text for `OTHER` only; the UI language never becomes domain state.
   */
  const decline = useCallback((reasonCode: CallDeclineReasonCode = 'NO_REASON', note: string | null = null) => {
    const call = machineRef.current.call;
    stopRingtone();
    diagnosticsRef.current.record('call.declined', { reasonCode });
    apply({ type: 'decline' });
    if (!call) return;
    // Fire-and-forget by design: the local ring has already stopped, and a
    // realtime failure must not turn a decline into an error for the user. The
    // caller's own screen falls back to the ring timeout. The note is never
    // logged — only the stable code is recorded above.
    void declineConsultationCall({
      bookingId: call.bookingId,
      sessionId: call.sessionId,
      reasonCode,
      note,
    }).then(result => {
      if (!result.ok) diagnosticsRef.current.record('call.decline-undelivered');
    });
  }, [apply]);

  /** Clear the terminal state (e.g. after the banner has been dismissed). */
  const dismiss = useCallback(() => {
    stopRingtone();
    apply({ type: 'reset' });
  }, [apply]);

  /** Retry audibility after a gesture — never an automatic retry loop. */
  const enableSound = useCallback((): boolean => {
    unlockRingtone();
    const ok = startRingtone();
    diagnosticsRef.current.record(ok ? 'ring.started' : 'ring.blocked');
    setRingBlocked(!ok);
    return ok;
  }, []);

  return {
    /** The validated invite, or null. */
    call: machine.call,
    state: machine.state,
    ringBlocked,
    accept,
    decline,
    /** Stop the ring tone only (nothing is answered or declined). */
    silence,
    dismiss,
    enableSound,
    /** Opt-in evidence; empty unless diagnostics are on. */
    diagnosticsSummary: () => diagnosticsRef.current.summary(),
  };
}
